'use client';

import { useToast } from '@/app/context/toast-provider';
import { ConfirmDialogTransition } from '@/components/ui/ConfirmDialog/ConfirmDialogTransition';
import { Button, Dialog, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useCallback, useEffect, useRef } from 'react';
import { LuCopy, LuDownload, LuX } from 'react-icons/lu';
import { useCopyToClipboard } from 'usehooks-ts';
const QR_SIZE = 176;
const ShareLinkPanel = ({
  link,
  caption,
  fileName,
  onCopy
}) => {
  const canvasRef = useRef(null);
  const handleDownloadQr = () => {
    const canvas = canvasRef.current;
    if (!canvas || !link) return;
    const anchor = document.createElement('a');
    anchor.href = canvas.toDataURL('image/png');
    anchor.download = fileName;
    anchor.click();
  };
  return <div className="flex flex-col gap-4">
            <div className="rounded-xl bg-brand/5! p-3 pt-4">
                <p className="mb-2 text-xs text-center font-medium text-foreground-muted">{caption}</p>
                <TextField value={link} fullWidth size="small" InputProps={{
        readOnly: true,
        endAdornment: <InputAdornment position="end">
                                <Button size="small" variant="text" color="brand" onClick={() => onCopy(link)} startIcon={<LuCopy className="size-3.5!" />} className="shrink-0! font-semibold! whitespace-nowrap!" sx={{
            textTransform: 'none'
          }}>
                                    Copy
                                </Button>
                            </InputAdornment>
      }} inputProps={{
        className: 'text-sm! text-foreground! truncate!',
        'aria-label': 'Shareable link'
      }} />

                <Divider className="my-4!">
                    <Typography variant="body2" color="text.secondary" className="text-center!">
                        or
                    </Typography>
                </Divider>

                <div className="flex flex-col items-center gap-3">
                    {link ? <QRCodeCanvas ref={canvasRef} value={link} size={QR_SIZE} level="H" marginSize={2} bgColor="#ffffff" fgColor="#18181b" className="rounded-md!" /> : <div className="flex size-40 items-center justify-center rounded-lg bg-muted text-sm text-foreground-muted">No link available</div>}
                    <Button size="small" variant="text" color="brand" disabled={!link} onClick={handleDownloadQr} startIcon={<LuDownload className="size-3.5!" />} className="font-semibold!" sx={{
          textTransform: 'none'
        }} fullWidth>
                        Download QR code
                    </Button>
                </div>
            </div>
        </div>;
};

/** Share a link: copy it, or show and download it as a QR code. */
export const ShareLinkDialog = ({
  open,
  onClose,
  link,
  title,
  linkCaption,
  fileName
}) => {
  const {
    showSuccessToast
  } = useToast();
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  useEffect(() => {
    if (copiedText) {
      showSuccessToast('Link copied to clipboard');
    }
  }, [copiedText, showSuccessToast]);
  const handleCopy = useCallback(value => {
    if (!value) return;
    void copyToClipboard('');
    void copyToClipboard(value);
  }, [copyToClipboard]);
  const slug = (fileName || 'link').replace(/[^\w-]+/g, '-').toLowerCase();
  return <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slots={{
    transition: ConfirmDialogTransition
  }} slotProps={{
    transition: {
      appear: true
    },
    paper: {
      sx: {
        borderRadius: '8px'
      }
    }
  }}>
            <div className="relative px-6 pt-5 pb-2">
                <IconButton size="small" onClick={onClose} className="absolute! top-4! right-4!" aria-label="Close">
                    <LuX className="text-lg!" />
                </IconButton>

                <div className="flex flex-col gap-3 pb-2">
                    <div className="flex flex-col gap-0.5">
                        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
                        <p className="text-sm text-foreground-muted">Copy a link or download a QR code</p>
                    </div>
                </div>
            </div>

            <div className="px-4">
                <ShareLinkPanel link={link} caption={linkCaption} fileName={`${slug}-qr.png`} onCopy={handleCopy} />
            </div>

            <div className="flex items-center justify-end gap-2 px-4 pt-4 pb-4">
                <Button variant="contained" color="neutral" fullWidth disableElevation onClick={onClose} sx={{
        textTransform: 'none'
      }} className="font-semibold!">
                    Close
                </Button>
            </div>
        </Dialog>;
};